import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Tooltip,
  Typography,
  useTheme,
  type SxProps,
  type Theme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAnimatedNumber } from '../../hooks/useAnimatedNumber';
import Co2OutlinedIcon from '@mui/icons-material/Co2Outlined';
import MonitorWeightOutlinedIcon from '@mui/icons-material/MonitorWeightOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SkipNextOutlinedIcon from '@mui/icons-material/SkipNextOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import type { OrganizationCarbonAvoidanceReport } from '../../types/carbon-avoidance-report.type';
import type { EsgCarbonWidgetId } from '../../types/dashboard-customize.type';
import {
  formatTco2e,
  formatTonnes,
  mapReportStreamsToChartRows,
} from '../../utils/carbonAvoidance.util';
import CarbonAvoidedByMaterialTable from './CarbonAvoidedByMaterialTable';
import CarbonAvoidedByStreamChart from './CarbonAvoidedByStreamChart';
import CarbonAvoidanceByStreamTable from '../report/CarbonAvoidanceByStreamTable';

interface CarbonAvoidanceDetailViewProps {
  report: OrganizationCarbonAvoidanceReport;
  periodLabel: string;
  visibleWidgets?: readonly EsgCarbonWidgetId[];
}

const statValueSx = {
  fontWeight: 700,
  lineHeight: 1.15,
  fontSize: { xs: '1.5rem', sm: '1.75rem' },
} as const;

const statCardSx = {
  p: '12px',
  pr: 1.25,
  borderRadius: '12px',
  border: '1px solid',
  borderColor: 'divider',
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1.25,
} as const;

const statContentSx = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
} as const;

function StatIconBox({
  icon,
  color,
}: {
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: '10px',
        bgcolor: alpha(color, 0.1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        '& .MuiSvgIcon-root': {
          fontSize: 24,
          color,
        },
      }}
    >
      {icon}
    </Box>
  );
}

function AnimatedStatValue({
  value,
  format,
  unit,
  sx,
}: {
  value: number;
  format: (animated: number) => string;
  unit?: string;
  sx?: SxProps<Theme>;
}) {
  const animated = useAnimatedNumber(value);

  return (
    <Typography sx={{ ...statValueSx, ...sx, mt: 0.25 }}>
      {format(animated)}
      {unit && (
        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{ ml: 0.5, fontWeight: 500, fontSize: '0.85rem' }}
        >
          {unit}
        </Typography>
      )}
    </Typography>
  );
}

function SummaryStat({
  label,
  value,
  format,
  unit,
  tooltip,
  icon,
  color,
  valueColor,
}: {
  label: string;
  value: number;
  format: (animated: number) => string;
  unit?: string;
  tooltip?: string;
  icon: React.ReactNode;
  color: string;
  valueColor?: string;
}) {
  const content = (
    <Paper sx={statCardSx}>
      <StatIconBox icon={icon} color={color} />
      <Box sx={statContentSx}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, fontSize: '0.68rem', display: 'block', lineHeight: 1.3 }}
        >
          {label}
        </Typography>
        <AnimatedStatValue
          value={value}
          format={format}
          unit={unit}
          sx={{ color: valueColor ?? 'text.primary' }}
        />
      </Box>
    </Paper>
  );
  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
}

const CarbonAvoidanceDetailView: React.FC<CarbonAvoidanceDetailViewProps> = ({
  report,
  periodLabel,
  visibleWidgets,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { executive_summary: summary } = report;
  const skipped = summary.skipped_run_count ?? 0;
  const failed = summary.failed_run_count ?? 0;
  const notCounted = skipped + failed;
  const chartStreams = mapReportStreamsToChartRows(report.by_stream);

  const co2Color = theme.palette.primary.main;
  const weightColor = theme.palette.info.main;
  const completedColor = theme.palette.success.main;
  const skippedColor = theme.palette.warning.main;
  const energyColor = theme.palette.secondary.main;
  const byStreamTitle = t('carbonAvoidance.byStreamTitle');
  const show = (id: EsgCarbonWidgetId) =>
    !visibleWidgets || visibleWidgets.includes(id);
  const showSummary =
    show('total_avoided') ||
    show('weight_diverted') ||
    show('completed_runs') ||
    show('skipped_runs');
  const showChart = show('stream_chart');
  const showTables = show('stream_table') || show('material_table');
  const summaryCardCount =
    Number(show('total_avoided')) +
    Number(show('weight_diverted')) +
    Number(showSummary) +
    Number(show('completed_runs')) +
    Number(show('skipped_runs'));

  if (!showSummary && !showChart && !showTables) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {(showSummary || showChart) && (
        <Grid
          container
          spacing={2}
          alignItems="stretch"
          sx={{ minHeight: { md: showSummary && showChart ? 360 : undefined } }}
        >
          {showSummary && (
            <Grid item xs={12} md={showChart ? 3 : 12} sx={{ display: 'flex' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateRows: showChart ? `repeat(${Math.max(summaryCardCount, 1)}, 1fr)` : undefined,
                  gridTemplateColumns: showChart
                    ? undefined
                    : {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        md: `repeat(${Math.min(Math.max(summaryCardCount, 1), 5)}, minmax(0, 1fr))`,
                      },
                  gap: 1,
                  width: '100%',
                  height: '100%',
                }}
              >
                {show('total_avoided') && (
                  <Paper sx={{ ...statCardSx, boxShadow: theme.shadows[1] }}>
                    <StatIconBox icon={<Co2OutlinedIcon />} color={co2Color} />
                    <Box sx={statContentSx}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, fontSize: '0.68rem', display: 'block', lineHeight: 1.3 }}
                      >
                        {t('carbonAvoidance.totalAvoided', { period: periodLabel })}
                      </Typography>
                      <AnimatedStatValue
                        value={summary.total_avoided_tco2e}
                        format={(n) => formatTco2e(n)}
                        unit={t('units.tco2e')}
                        sx={{
                          color: 'text.primary',
                          fontSize: { xs: '1.65rem', sm: '2rem' },
                        }}
                      />
                    </Box>
                  </Paper>
                )}

                {show('weight_diverted') && (
                  <SummaryStat
                    label={t('carbonAvoidance.weightDiverted')}
                    value={summary.total_weight_diverted_tonnes}
                    format={(n) => formatTonnes(n)}
                    unit={t('units.tonnes')}
                    icon={<MonitorWeightOutlinedIcon />}
                    color={weightColor}
                  />
                )}
                <SummaryStat
                  label={t('carbonAvoidance.energySaved')}
                  value={summary.total_energy_saved_kwh ?? 0}
                  format={(n) => Math.round(n).toLocaleString('en-US')}
                  unit={t('units.kwh')}
                  icon={<BoltOutlinedIcon />}
                  color={energyColor}
                />
                {show('completed_runs') && (
                  <SummaryStat
                    label={t('carbonAvoidance.completedRuns')}
                    value={summary.completed_run_count}
                    format={(n) => Math.round(n).toLocaleString('en-US')}
                    icon={<CheckCircleOutlineIcon />}
                    color={completedColor}
                  />
                )}
                {show('skipped_runs') && (
                  <SummaryStat
                    label={t('carbonAvoidance.notCountedRuns')}
                    value={notCounted}
                    format={(n) => Math.round(n).toLocaleString('en-US')}
                    icon={<SkipNextOutlinedIcon />}
                    color={skippedColor}
                    tooltip={
                      notCounted > 0
                        ? t('carbonAvoidance.notCountedTooltip', { skipped, failed })
                        : undefined
                    }
                  />
                )}
              </Box>
            </Grid>
          )}

          {showChart && (
            <Grid item xs={12} md={showSummary ? 9 : 12} sx={{ display: 'flex' }}>
              {chartStreams.length > 0 ? (
                <Box sx={{ width: '100%', height: '100%' }}>
                  <CarbonAvoidedByStreamChart
                    streams={chartStreams}
                    title={byStreamTitle}
                    chartType="bar"
                    horizontalBar
                    hideMenu
                    fillHeight
                  />
                </Box>
              ) : (
                <Paper
                  sx={{
                    p: 2,
                    width: '100%',
                    height: '100%',
                    borderRadius: '16px',
                    boxShadow: theme.shadows[2],
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {byStreamTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.alerts.noCarbonByStream')}
                  </Typography>
                </Paper>
              )}
            </Grid>
          )}
        </Grid>
      )}

      {showTables && (
        <Grid container spacing={2} alignItems="stretch">
          {show('stream_table') && (
            <Grid item xs={12} md={show('material_table') ? 6 : 12} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%', height: '100%' }}>
                <CarbonAvoidanceByStreamTable rows={report.by_stream} compact fillHeight />
              </Box>
            </Grid>
          )}
          {show('material_table') && (
            <Grid item xs={12} md={show('stream_table') ? 6 : 12} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%', height: '100%' }}>
                <CarbonAvoidedByMaterialTable rows={report.by_material} fillHeight />
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default CarbonAvoidanceDetailView;
